import {
    Component,
    ChangeDetectionStrategy,
    signal,
    computed,
    inject,
    OnInit,
    DestroyRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    AppFrameworkContextService,
    BizEntityStateStoreService,
    BizEntityTypeStateStoreService,
    SystemDataCreationService,
    sendInBatches,
    buildLogDates,
    buildNoisySeries,
} from '@optima-stride-ai/optima-app-framework';
import { UserContextService, UserStateStoreService } from '@optima-stride-ai/optima-platform';
import { IBizEntity, IBizEntityType, IUser, Units, BizEntityTypeCategory, Unit, FieldTypes, ItxnRelationshipLog, IAdditionalInfo, IFieldMeta } from '@optima-stride-ai/core';
import { ObjectiveStateStoreService } from '../objective/objective-state-store/objective-state-store.service';
import { IObjective } from '../objective/objective.model';
import { DateRange } from '../../models/interfaces/date-range-model';
import { DeliverableStateStoreService } from '../deliverables/deliverable-state-store/deliverable-state-store.service';
import { IDeliverable } from '../deliverables/deliverable.model';
import { ValueRange } from '../deliverables/value-range-model';
import { PlanStateStoreService } from '../deliverables/plan-state-store/plan-state-store.service';
import { iPlanItems } from '../deliverables/progress-plan.model';
import { KpiStateStoreService } from '../kpi/KPI-state-stores/kpi-state-store.service';
import { IKPI } from '../kpi/kpi.model';
import { AutoCreateEntitiesService } from '../kpi/auto-create-entites';
import { MeasuresStateStoreService } from '../transaction(Measure)/measure-state-store/measures-state-store.service';
import { KPIAndMeasureCategories, IMeasure } from '../transaction(Measure)/measure.model';
import { MeasureLogStateStoreService } from '../transaction-log/measure-log-state-store/measure-log-state-store.service';
import { REALISTIC_DELIVERABLE_DEFS } from './realistic-kpi-measure.data';
import { SOFTWARE_MEASURE_DEFS, SOFTWARE_MEASURE_PACK_NAME, SOFTWARE_MEASURE_DATA_START, SoftwareMeasureDef } from './software-measure-pack.data';
import { SOFTWARE_MEASURE_DELIVERABLE_DEFS, SOFTWARE_MEASURE_KPI_COUNT, SoftwareMeasureDeliverableDef } from './software-measure-pack-deliverable.data';

// ─────────────────────────────────────────────────────────────────────────────
// Local types — mirror the library's multi-config split (not re-exported from
// the installed package, so defined locally here).
//
// BenzaitenDateRangeConfig  → cross-pack, always passed (smoke / edge / perm)
// BenzaitenDenseConfig      → count fields, only for the dense pack
// ─────────────────────────────────────────────────────────────────────────────

export type BenzaitenDateRangeMode = 'around45' | 'around90' | 'custom';

interface BenzaitenDateRangeConfig {
    dateRangeMode: BenzaitenDateRangeMode;
    customStartDate?: string;
    customEndDate?: string;
}

interface BenzaitenDenseConfig extends BenzaitenDateRangeConfig {
    objectiveCount: number;
    deliverablesPerObjective: number;
    kpiCount: number;
    measureCount: number;
}

const DEFAULT_DATE_RANGE_CONFIG: BenzaitenDateRangeConfig = {
    dateRangeMode: 'around45',
};

const DEFAULT_DENSE_CONFIG: BenzaitenDenseConfig = {
    dateRangeMode: 'around45',
    objectiveCount: 3,
    deliverablesPerObjective: 2,
    kpiCount: 3,
    measureCount: 3,
};

/** Resolves dateRangeMode to concrete ISO date strings (YYYY-MM-DD). */
function resolveDateRange(cfg: BenzaitenDateRangeConfig): { start: string; end: string } {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const iso = (d: Date) => d.toISOString().split('T')[0];
    if (cfg.dateRangeMode === 'custom') {
        const fb45Start = iso(new Date(today.getTime() - 45 * 86_400_000));
        const fb45End   = iso(new Date(today.getTime() + 45 * 86_400_000));
        return { start: cfg.customStartDate ?? fb45Start, end: cfg.customEndDate ?? fb45End };
    }
    const days = cfg.dateRangeMode === 'around90' ? 90 : 45;
    return {
        start: iso(new Date(today.getTime() - days * 86_400_000)),
        end:   iso(new Date(today.getTime() + days * 86_400_000)),
    };
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Local-time yyyy-mm-dd for a Date (used for date inputs). */
function toISODate(d: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Midnight (local) of the given epoch ms. */
function startOfDayMs(ms: number): number {
    const d = new Date(ms);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

/** Last millisecond (local) of the given epoch ms' day. */
function endOfDayMs(ms: number): number {
    const d = new Date(ms);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
}

/**
 * Formats a shifted Date back into the same textual shape as the original value:
 * "yyyy-mm-dd HH:mm" (or "yyyy-mm-dd") for ISO-style inputs, otherwise "DD Mon YYYY".
 */
function formatDateLikeOriginal(original: string, shifted: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    if (/^\d{4}-\d{2}-\d{2}/.test(original)) {
        const base = `${shifted.getFullYear()}-${p(shifted.getMonth() + 1)}-${p(shifted.getDate())}`;
        return /\d{1,2}:\d{2}/.test(original)
            ? `${base} ${p(shifted.getHours())}:${p(shifted.getMinutes())}`
            : base;
    }
    return `${p(shifted.getDate())} ${SHORT_MONTHS[shifted.getMonth()]} ${shifted.getFullYear()}`;
}

interface PackItem {
    id: string;
    displayName: string;
    description: string;
    requiresPackConfig: boolean;
}

interface LogEntry {
    type: 'info' | 'success' | 'error' | 'warning';
    message: string;
    timestamp: string;
}

@Component({
    selector: 'optima-benzaiten-data-gen-panel',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule],
    host: { class: 'd-flex flex-column gap-3 p-3' },
    template: `
        <div class="d-flex align-items-center gap-2">
            <h5 class="mb-0">Benzaiten OKR — Data Generation</h5>
            <span class="badge bg-warning text-dark">dev / uat only</span>
        </div>
        <p class="text-muted small mb-0">
            Generates seed + test data for the Benzaiten app. This will <strong>delete all existing app data</strong>
            before creating fresh data. Use only in non-production environments.
        </p>

        <!-- ── Section 0: Reset Structure ──────────────────────────────────── -->
        <div class="card">
            <div class="card-header fw-semibold">Reset Structure</div>
            <div class="card-body d-flex flex-column gap-2">
                <p class="text-muted small mb-1">
                    Wipes all existing tenant data and re-creates the app structure (Objectives, Check-ins, etc.) without generating any test pack data.
                    Use this to reset to a clean baseline.
                </p>
                <div class="alert alert-warning py-2 small mb-2">
                    ⚠️ This permanently deletes all existing data for the current tenant.
                </div>
                <div class="form-check mb-1">
                    <input
                        class="form-check-input"
                        type="checkbox"
                        id="confirm-seed-benzaiten"
                        [ngModel]="confirmSeed()"
                        (ngModelChange)="confirmSeed.set($event)"
                        [disabled]="isSeedingOnly()"
                    />
                    <label class="form-check-label small" for="confirm-seed-benzaiten">
                        I understand this will <strong>delete existing data</strong> and re-create a fresh structure
                    </label>
                </div>
                <div class="d-flex align-items-center gap-3">
                    <button
                        class="btn btn-warning btn-sm"
                        [disabled]="isSeedingOnly() || !confirmSeed()"
                        (click)="seedOnly()"
                    >
                        @if (isSeedingOnly()) {
                            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                            Seeding…
                        } @else {
                            Reset Structure
                        }
                    </button>
                </div>
                @if (seedResult(); as sr) {
                    <div
                        class="alert py-2 small mb-0"
                        [class.alert-success]="sr.success"
                        [class.alert-danger]="!sr.success"
                    >
                        @if (sr.success) { ✅ Structure reset successfully. }
                        @else { ❌ {{ sr.error ?? 'Failed' }} }
                    </div>
                }
            </div>
        </div>

        <!-- ── Section: 33 Software Measure Pack ───────────────────────────── -->
        <div class="card">
            <div class="card-header fw-semibold">{{ softwareMeasurePackName }}</div>
            <div class="card-body d-flex flex-column gap-2">
                <p class="text-muted small mb-1">
                    Creates {{ softwareMeasureDefCount }} software-delivery measures (velocity, DORA, quality &amp;
                    reliability metrics) with proper KPI bizRules, each rebased to the <strong>Start date</strong>
                    (T + n days) and capped at the <strong>End date</strong>. Then creates
                    {{ softwareMeasureKpiCount }} objectives (one per KPI) with 3 quarterly deliverables + plans
                    — {{ softwareMeasureDeliverableCount }} deliverables in total. Requires
                    <strong>Reset Structure</strong> to have been run first. Non-destructive.
                </p>
                <div class="row g-2">
                    <div class="col-sm-6">
                        <label for="swPackStart" class="form-label small mb-1">Start date (T)</label>
                        <input
                            id="swPackStart"
                            type="date"
                            class="form-control form-control-sm"
                            [ngModel]="swPackStartDate()"
                            (ngModelChange)="swPackStartDate.set($event)"
                            [disabled]="isGeneratingSoftwareMeasurePack()"
                        />
                    </div>
                    <div class="col-sm-6">
                        <label for="swPackEnd" class="form-label small mb-1">End date</label>
                        <input
                            id="swPackEnd"
                            type="date"
                            class="form-control form-control-sm"
                            [ngModel]="swPackEndDate()"
                            (ngModelChange)="swPackEndDate.set($event)"
                            [disabled]="isGeneratingSoftwareMeasurePack()"
                        />
                    </div>
                </div>
                <!-- Entity Creation Toggles -->
                <div class="border rounded p-3 bg-light">
                    <div class="fw-semibold small mb-2">Entity Creation Options</div>
                    <div class="d-flex flex-column gap-2 small">
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                id="swPackCreateMeasure"
                                [checked]="swPackCreateMeasure()"
                                (change)="swPackCreateMeasure.set($event.target.checked)"
                                [disabled]="isGeneratingSoftwareMeasurePack()"
                            />
                            <label class="form-check-label" for="swPackCreateMeasure">
                                Create Measures
                            </label>
                        </div>
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                id="swPackCreateKPI"
                                [checked]="swPackCreateKPI()"
                                (change)="swPackCreateKPI.set($event.target.checked)"
                                [disabled]="isGeneratingSoftwareMeasurePack() || !swPackCreateMeasure()"
                            />
                            <label class="form-check-label" for="swPackCreateKPI">
                                Create KPIs
                                @if (!swPackCreateMeasure()) {
                                    <span class="text-muted small ms-1">(requires Measures)</span>
                                }
                            </label>
                        </div>
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                id="swPackCreateDeliverable"
                                [checked]="swPackCreateDeliverable()"
                                (change)="swPackCreateDeliverable.set($event.target.checked)"
                                [disabled]="isGeneratingSoftwareMeasurePack() || !swPackCreateKPIEffective()"
                            />
                            <label class="form-check-label" for="swPackCreateDeliverable">
                                Create Deliverables
                                @if (!swPackCreateKPIEffective()) {
                                    <span class="text-muted small ms-1">(requires KPIs)</span>
                                }
                            </label>
                        </div>
                        <div class="form-check">
                            <input
                                class="form-check-input"
                                type="checkbox"
                                id="swPackCreateMeasureLog"
                                [checked]="swPackCreateMeasureLog()"
                                (change)="swPackCreateMeasureLog.set($event.target.checked)"
                                [disabled]="isGeneratingSoftwareMeasurePack() || !swPackCreateMeasure()"
                            />
                            <label class="form-check-label" for="swPackCreateMeasureLog">
                                Create Measure Logs
                                @if (!swPackCreateMeasure()) {
                                    <span class="text-muted small ms-1">(requires Measures)</span>
                                }
                            </label>
                        </div>
                    </div>
                </div>

                <div class="d-flex align-items-center gap-3">
                    <button
                        class="btn btn-primary btn-sm"
                        [disabled]="isGeneratingSoftwareMeasurePack()"
                        (click)="generateSoftwareMeasurePack()"
                    >
                        @if (isGeneratingSoftwareMeasurePack()) {
                            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
                            Generating…
                        } @else {
                            Generate {{ softwareMeasurePackName }}
                        }
                    </button>
                </div>
            </div>
        </div>

        <!-- ── Section 1: Pack Selector ────────────────────────────────────── -->
        <div class="card">
            <div class="card-header fw-semibold">1. Select Test Pack</div>
            <div class="card-body d-flex flex-column gap-2">
                @for (pack of packs(); track pack.id) {
                    <div
                        class="form-check p-3 border rounded"
                        [class.border-primary]="selectedPackId() === pack.id"
                        [class.bg-primary-subtle]="selectedPackId() === pack.id"
                        (click)="selectedPackId.set(pack.id)"
                        (keydown.enter)="selectedPackId.set(pack.id)"
                        (keydown.space)="selectedPackId.set(pack.id)"
                        role="radio"
                        [attr.aria-checked]="selectedPackId() === pack.id"
                        tabindex="0"
                    >
                        <input
                            class="form-check-input"
                            type="radio"
                            [id]="'pack-' + pack.id"
                            [value]="pack.id"
                            [checked]="selectedPackId() === pack.id"
                            (change)="selectedPackId.set(pack.id)"
                            aria-hidden="true"
                        />
                        <label class="form-check-label d-flex flex-column gap-1" [for]="'pack-' + pack.id">
                            <span class="fw-semibold">{{ pack.displayName }}</span>
                            <span class="text-muted small">{{ pack.description }}</span>
                        </label>
                    </div>
                }
                @if (packs().length === 0) {
                    <p class="text-muted small mb-0">No test packs available for this app context.</p>
                }
            </div>
        </div>

        <!-- ── Section 2: Date Range (always shown — applies to all packs) ─── -->
        <div class="card">
            <div class="card-header fw-semibold">2. Date Range</div>
            <div class="card-body d-flex flex-column gap-3">
                <p class="text-muted small mb-0">
                    Controls the window used for Objective / Deliverable durations and
                    Check In entity names. Applied to every pack.
                </p>

                <!-- Preset buttons -->
                <div class="d-flex gap-2 flex-wrap" role="group" aria-label="Date range presets">
                    <button
                        type="button"
                        class="btn btn-sm"
                        [class.btn-primary]="dateRangeConfig().dateRangeMode === 'around45'"
                        [class.btn-outline-secondary]="dateRangeConfig().dateRangeMode !== 'around45'"
                        (click)="setDateRangeMode('around45')"
                    >±45 days</button>
                    <button
                        type="button"
                        class="btn btn-sm"
                        [class.btn-primary]="dateRangeConfig().dateRangeMode === 'around90'"
                        [class.btn-outline-secondary]="dateRangeConfig().dateRangeMode !== 'around90'"
                        (click)="setDateRangeMode('around90')"
                    >±90 days</button>
                    <button
                        type="button"
                        class="btn btn-sm"
                        [class.btn-primary]="dateRangeConfig().dateRangeMode === 'custom'"
                        [class.btn-outline-secondary]="dateRangeConfig().dateRangeMode !== 'custom'"
                        (click)="setDateRangeMode('custom')"
                    >Custom</button>
                </div>

                <!-- Custom date pickers (visible only in custom mode) -->
                @if (dateRangeConfig().dateRangeMode === 'custom') {
                    <div class="row g-3">
                        <div class="col-sm-6">
                            <label for="customStart" class="form-label">Start date</label>
                            <input
                                id="customStart"
                                type="date"
                                class="form-control"
                                [ngModel]="dateRangeConfig().customStartDate"
                                (ngModelChange)="setCustomDate('customStartDate', $event)"
                            />
                        </div>
                        <div class="col-sm-6">
                            <label for="customEnd" class="form-label">End date</label>
                            <input
                                id="customEnd"
                                type="date"
                                class="form-control"
                                [ngModel]="dateRangeConfig().customEndDate"
                                (ngModelChange)="setCustomDate('customEndDate', $event)"
                            />
                        </div>
                    </div>
                }

                <!-- Resolved range preview -->
                <div class="alert alert-secondary py-2 mb-0 small">
                    <strong>Window:</strong>
                    {{ resolvedRange().start }}
                    <span class="mx-1 text-muted">→</span>
                    {{ resolvedRange().end }}
                    <span class="ms-2 text-muted">({{ resolvedDays() }} days)</span>
                </div>
            </div>
        </div>

        <!-- ── Section 3: Dense pack config (only when dense selected) ──────── -->
        @if (requiresConfig()) {
            <div class="card">
                <div class="card-header fw-semibold">3. Dense Pack — Entity Counts</div>
                <div class="card-body">
                    <div class="row g-3">
                        <div class="col-md-6">
                            <label for="objCount" class="form-label">
                                Objectives <span class="text-muted">(1–10)</span>
                            </label>
                            <input
                                id="objCount"
                                type="number"
                                class="form-control"
                                [min]="1" [max]="10"
                                [ngModel]="denseConfig().objectiveCount"
                                (ngModelChange)="setDenseField('objectiveCount', $event)"
                            />
                        </div>
                        <div class="col-md-6">
                            <label for="delPerObj" class="form-label">
                                Deliverables / Objective <span class="text-muted">(1–5)</span>
                            </label>
                            <input
                                id="delPerObj"
                                type="number"
                                class="form-control"
                                [min]="1" [max]="5"
                                [ngModel]="denseConfig().deliverablesPerObjective"
                                (ngModelChange)="setDenseField('deliverablesPerObjective', $event)"
                            />
                        </div>
                        <div class="col-md-6">
                            <label for="kpiCount" class="form-label">
                                KPIs <span class="text-muted">(0–10)</span>
                            </label>
                            <input
                                id="kpiCount"
                                type="number"
                                class="form-control"
                                [min]="0" [max]="10"
                                [ngModel]="denseConfig().kpiCount"
                                (ngModelChange)="setDenseField('kpiCount', $event)"
                            />
                        </div>
                        <div class="col-md-6">
                            <label for="msrCount" class="form-label">
                                Measures <span class="text-muted">(0–10)</span>
                            </label>
                            <input
                                id="msrCount"
                                type="number"
                                class="form-control"
                                [min]="0" [max]="10"
                                [ngModel]="denseConfig().measureCount"
                                (ngModelChange)="setDenseField('measureCount', $event)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        }

        <!-- ── Live Summary ──────────────────────────────────────────────────── -->
        @if (selectedPackId()) {
            <div class="alert alert-info mb-0" role="status">
                <strong>Summary:</strong> {{ summary() }}
            </div>
        }

        <!-- ── Section: Team User Selection ──────────────────────────────────── -->
        <div class="card">
            <div class="card-header fw-semibold">Team Users</div>
            <div class="card-body d-flex flex-column gap-2">
                <p class="text-muted small mb-1">
                    Select users for each system team created during generation.
                    Unchecked users will be skipped for that team only.
                </p>
                @if (isLoadingUsers()) {
                    <div class="d-flex align-items-center gap-2 text-muted small">
                        <span class="spinner-border spinner-border-sm" role="status"></span>
                        Loading users…
                    </div>
                } @else if (availableUsers().length === 0) {
                    <p class="text-muted small mb-0">No users found.</p>
                } @else {
                    <div class="border rounded p-2">
                        <div class="fw-semibold small mb-2">Admin Team</div>
                        <div class="d-flex gap-2 mb-2">
                            <button type="button" class="btn btn-outline-secondary btn-sm" (click)="selectAllTeamUsers('admin')">Select all</button>
                            <button type="button" class="btn btn-outline-secondary btn-sm" (click)="deselectAllTeamUsers('admin')">Deselect all</button>
                        </div>
                        @for (user of availableUsers(); track user.name) {
                            <div class="form-check">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    [id]="'admin-user-' + user.name"
                                    [checked]="isTeamUserSelected('admin', user.name)"
                                    (change)="toggleTeamUser('admin', user.name)"
                                />
                                <label class="form-check-label" [for]="'admin-user-' + user.name">
                                    <span class="fw-semibold">{{ user.name || 'Unnamed' }}</span>
                                    @if (user.email) {
                                        <span class="text-muted ms-2 small">{{ user.email }}</span>
                                    }
                                </label>
                            </div>
                        }
                        <div class="text-muted small mt-1">
                            {{ selectedTeamUserCount('admin') }} of {{ availableUsers().length }} users selected
                        </div>
                    </div>

                    <div class="border rounded p-2">
                        <div class="fw-semibold small mb-2">Basic Team</div>
                        <div class="d-flex gap-2 mb-2">
                            <button type="button" class="btn btn-outline-secondary btn-sm" (click)="selectAllTeamUsers('basic')">Select all</button>
                            <button type="button" class="btn btn-outline-secondary btn-sm" (click)="deselectAllTeamUsers('basic')">Deselect all</button>
                        </div>
                        @for (user of availableUsers(); track user.name) {
                            <div class="form-check">
                                <input
                                    class="form-check-input"
                                    type="checkbox"
                                    [id]="'basic-user-' + user.name"
                                    [checked]="isTeamUserSelected('basic', user.name)"
                                    (change)="toggleTeamUser('basic', user.name)"
                                />
                                <label class="form-check-label" [for]="'basic-user-' + user.name">
                                    <span class="fw-semibold">{{ user.name || 'Unnamed' }}</span>
                                    @if (user.email) {
                                        <span class="text-muted ms-2 small">{{ user.email }}</span>
                                    }
                                </label>
                            </div>
                        }
                        <div class="text-muted small mt-1">
                            {{ selectedTeamUserCount('basic') }} of {{ availableUsers().length }} users selected
                        </div>
                    </div>
                }
            </div>
        </div>

        <!-- ── Generate Button ───────────────────────────────────────────────── -->
        <div class="d-flex align-items-center gap-3">
            <button
                class="btn btn-primary"
                [disabled]="!selectedPackId() || isGenerating()"
                (click)="generate()"
                aria-label="Generate data"
            >
                @if (isGenerating()) {
                    <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Generating…
                } @else {
                    Generate Data
                }
            </button>
            @if (logs().length > 0 && !isGenerating()) {
                <button class="btn btn-outline-secondary btn-sm" (click)="clearLogs()" aria-label="Clear logs">
                    Clear Logs
                </button>
            }
        </div>

        <!-- ── Log Output ────────────────────────────────────────────────────── -->
        @if (logs().length > 0) {
            <div class="card" role="log" aria-label="Generation log" aria-live="polite">
                <div class="card-header fw-semibold d-flex justify-content-between align-items-center">
                    <span>Log Output</span>
                    <span
                        class="badge"
                        [class.bg-success]="lastResultSuccess() === true"
                        [class.bg-danger]="lastResultSuccess() === false"
                    >
                        {{ lastResultSuccess() === true ? 'Success' : lastResultSuccess() === false ? 'Failed' : '' }}
                    </span>
                </div>
                <div class="card-body p-0">
                    <div class="overflow-auto" style="max-height: 300px; font-size: 0.8rem; font-family: monospace;">
                        @for (entry of logs(); track $index) {
                            <div
                                class="px-3 py-1 border-bottom"
                                [class.text-success]="entry.type === 'success'"
                                [class.text-danger]="entry.type === 'error'"
                                [class.text-warning]="entry.type === 'warning'"
                            >
                                <span class="text-muted me-2">{{ entry.timestamp }}</span>
                                {{ entry.message }}
                            </div>
                        }
                    </div>
                </div>
            </div>
        }
    `,
})
export class BenzaitenDataGenPanelComponent implements OnInit {
    private readonly svc = inject(SystemDataCreationService);
    private readonly contextService = inject(AppFrameworkContextService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly userStateStoreService = inject(UserStateStoreService);
    private readonly userContextService = inject(UserContextService);
    private readonly objectiveStateStore = inject(ObjectiveStateStoreService);
    private readonly bizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
    private readonly bizEntityStateStoreService = inject(BizEntityStateStoreService);
    private readonly deliverableStateStoreService = inject(DeliverableStateStoreService);
    private readonly kpiStateStoreService = inject(KpiStateStoreService);
    private readonly measuresStateStoreService = inject(MeasuresStateStoreService);
    private readonly planStateStoreService = inject(PlanStateStoreService);
    private readonly autoCreateEntitiesService = inject(AutoCreateEntitiesService);
    private readonly measureLogStateStoreService = inject(MeasureLogStateStoreService);

    readonly packs = signal<PackItem[]>([]);
    readonly selectedPackId = signal<string | undefined>(undefined);

    // ── Seed-only state ───────────────────────────────────────────────────────
    readonly confirmSeed = signal(false);
    readonly isSeedingOnly = signal(false);
    readonly seedResult = signal<{ success: boolean; error?: string } | null>(null);

    readonly isGenerating = signal(false);
    readonly logs = signal<LogEntry[]>([]);
    readonly lastResultSuccess = signal<boolean | undefined>(undefined);

    // ── 33 Software Measure Pack state ──────────────────────────────────────────
    readonly isGeneratingSoftwareMeasurePack = signal(false);
    readonly softwareMeasurePackName = SOFTWARE_MEASURE_PACK_NAME;
    readonly softwareMeasureDefCount = SOFTWARE_MEASURE_DEFS.length;
    readonly softwareMeasureDeliverableCount = SOFTWARE_MEASURE_DELIVERABLE_DEFS.length;
    readonly softwareMeasureKpiCount = SOFTWARE_MEASURE_KPI_COUNT;
    /**
     * Start date (T) each measure's log series is rebased to.
     */
    readonly swPackStartDate = signal(SOFTWARE_MEASURE_DATA_START);
    /** Upper bound for generated log dates. Capped at today. */
    readonly swPackEndDate = signal(toISODate(new Date()));

    // ── Software Measure Pack entity creation toggles ──────────────────────────
    /** Create Measure entities. */
    readonly swPackCreateMeasure = signal(true);
    /** Create KPI entities. Requires swPackCreateMeasure to be true. */
    readonly swPackCreateKPI = signal(true);
    /** Create Deliverable entities. Requires swPackCreateKPI and swPackCreateMeasure. */
    readonly swPackCreateDeliverable = signal(true);
    /** Create Measure Log entities. Requires swPackCreateMeasure to be true. */
    readonly swPackCreateMeasureLog = signal(true);

    // ── Derived toggle states with dependency logic ──────────────────────────────
    readonly swPackCreateMeasureEffective = computed(() => this.swPackCreateMeasure());

    readonly swPackCreateKPIEffective = computed(
        () => this.swPackCreateKPI() && this.swPackCreateMeasureEffective()
    );

    readonly swPackCreateDeliverableEffective = computed(
        () => this.swPackCreateDeliverable() && this.swPackCreateKPIEffective() && this.swPackCreateMeasureEffective()
    );

    readonly swPackCreateMeasureLogEffective = computed(
        () => this.swPackCreateMeasureLog() && this.swPackCreateMeasureEffective()
    );

    // ── Team user selection ────────────────────────────────────────────────────
    readonly availableUsers = signal<IUser[]>([]);
    readonly selectedAdminUserNames = signal<Set<string>>(new Set());
    readonly selectedBasicTeamUserNames = signal<Set<string>>(new Set());
    readonly isLoadingUsers = signal(false);

    // ── Two separate config signals ────────────────────────────────────────────
    /** Cross-pack date range — always passed to every pack factory. */
    readonly dateRangeConfig = signal<BenzaitenDateRangeConfig>({ ...DEFAULT_DATE_RANGE_CONFIG });
    /** Dense-pack-only entity counts — only relevant when dense is selected. */
    readonly denseConfig = signal<BenzaitenDenseConfig>({ ...DEFAULT_DENSE_CONFIG });

    // ── Derived ────────────────────────────────────────────────────────────────

    readonly selectedPack = computed(() =>
        this.packs().find(p => p.id === this.selectedPackId()),
    );

    readonly requiresConfig = computed(() =>
        this.selectedPack()?.requiresPackConfig ?? false,
    );

    /**
     * The config passed to the API:
     * - Dense pack → merged BenzaitenDenseConfig (date range + counts)
     * - All other packs → BenzaitenDateRangeConfig only
     */
    readonly packConfig = computed((): BenzaitenDateRangeConfig | BenzaitenDenseConfig => {
        if (this.requiresConfig()) {
            return { ...this.dateRangeConfig(), ...this.denseConfig() };
        }
        return this.dateRangeConfig();
    });

    readonly resolvedRange = computed(() => resolveDateRange(this.dateRangeConfig()));

    readonly resolvedDays = computed(() => {
        const { start, end } = this.resolvedRange();
        return Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86_400_000);
    });

    readonly summary = computed(() => {
        const pack = this.selectedPack();
        if (!pack) return 'Select a pack to see a summary.';
        const { start, end } = this.resolvedRange();
        if (pack.requiresPackConfig) {
            const d = this.denseConfig();
            const totalDeliverables = d.objectiveCount * d.deliverablesPerObjective;
            const isDense = true;
            const categoriesPerDeliverable = isDense ? 4 : 1;
            // Estimate weekly log count from start date to today, capped at 52
            const startDt = new Date(start);
            const today = new Date();
            const weeksFromStart = Math.max(0, Math.floor((today.getTime() - startDt.getTime()) / (7 * 24 * 60 * 60 * 1_000)));
            const logsPerMeasure = Math.min(weeksFromStart, 52);
            // Per deliverable: (Measure + KPI) × categories + getState + Deliverable + Plan + logs
            const requestsPerDeliverable =
                categoriesPerDeliverable * 2   // Measure + KPI per category
                + 1                            // single getState()
                + 1                            // Deliverable
                + 1                            // Plan
                + categoriesPerDeliverable * logsPerMeasure; // logs
            const totalRequests = d.objectiveCount + totalDeliverables * requestsPerDeliverable;
            return (
                `${d.objectiveCount} objective(s) × ${d.deliverablesPerObjective} deliverable(s) ` +
                `= ${totalDeliverables} deliverable(s), ${categoriesPerDeliverable} KPI+Measure per deliverable, ` +
                `~${logsPerMeasure} weekly logs per measure. ` +
                `Estimated API requests: ~${totalRequests.toLocaleString()} (sequential, batched logs). ` +
                `Window: ${start} → ${end}.`
            );
        }
        if (pack.id === 'realistic') {
            const REALISTIC_OBJECTIVES = 8;
            const REALISTIC_DELIVERABLES = 17;
            const REALISTIC_ORG_NODES = 13; // 4 top-level + 5 under General + 5 under Eng + 3 under Sales&Mkt (incl. Depts)
            const REALISTIC_CATEGORIES = 4;
            const startDt = new Date(start);
            const today = new Date();
            const weeksFromStart = Math.max(0, Math.floor((today.getTime() - startDt.getTime()) / (7 * 24 * 60 * 60 * 1_000)));
            const logsPerMeasure = Math.min(weeksFromStart, 52);
            const requestsPerDeliverable =
                REALISTIC_CATEGORIES * 2   // KPI + Measure per category
                + 1                        // single getState()
                + 1                        // Deliverable
                + 1                        // Plan
                + REALISTIC_CATEGORIES * logsPerMeasure;
            const totalRequests = REALISTIC_ORG_NODES + REALISTIC_OBJECTIVES + REALISTIC_DELIVERABLES * requestsPerDeliverable;
            return (
                `${REALISTIC_ORG_NODES} org nodes (Depts/Groups) + ${REALISTIC_OBJECTIVES} objectives × ${REALISTIC_DELIVERABLES} deliverables total, ` +
                `${REALISTIC_CATEGORIES} KPI+Measure per deliverable, ~${logsPerMeasure} weekly logs per measure. ` +
                `Estimated API requests: ~${totalRequests.toLocaleString()} (sequential, batched logs). ` +
                `Window: ${start} → ${end}.`
            );
        }
        return `${pack.description} Window: ${start} → ${end}.`;
    });

    // ── Lifecycle ──────────────────────────────────────────────────────────────

    ngOnInit(): void {
        this.loadPacks();

        // Seed custom date inputs to today ± 45 days
        const { start, end } = resolveDateRange({ dateRangeMode: 'around45' });
        this.dateRangeConfig.update(c => ({ ...c, customStartDate: start, customEndDate: end }));

        this.loadUsers();
    }

    private loadPacks(): void {
        // Read from the framework pack registry (via the service) so common packs
        // like 'deletion-flow' — registered in COMMON_TEST_DATA_PACKS and prepended
        // to every app's list by TEST_DATA_PACK_REGISTRY — show up in the selector
        // alongside Benzaiten-specific packs.
        this.packs.set(
            this.svc.getAvailableTestPacks().map(p => ({
                id: p.id,
                displayName: p.displayName,
                description: p.description,
                requiresPackConfig: p.requiresPackConfig,
            })),
        );
    }

    private async loadUsers(): Promise<void> {
        this.isLoadingUsers.set(true);
        try {
            const users = await this.userStateStoreService.getAll();
            this.availableUsers.set(users);
            // Select all users by default
            const allUserNames = new Set(users.map(u => u.name ?? '').filter(Boolean));
            this.selectedAdminUserNames.set(new Set(allUserNames));
            this.selectedBasicTeamUserNames.set(new Set(allUserNames));
        } catch {
            this.availableUsers.set([]);
        } finally {
            this.isLoadingUsers.set(false);
        }
    }

    // ── User actions ───────────────────────────────────────────────────────────

    setDateRangeMode(mode: BenzaitenDateRangeMode): void {
        this.dateRangeConfig.update(c => ({ ...c, dateRangeMode: mode }));
    }

    setCustomDate(field: 'customStartDate' | 'customEndDate', value: string): void {
        this.dateRangeConfig.update(c => ({ ...c, [field]: value }));
    }

    setDenseField(field: keyof Omit<BenzaitenDenseConfig, keyof BenzaitenDateRangeConfig>, value: number): void {
        this.denseConfig.update(d => ({ ...d, [field]: Number(value) }));
    }

    clearLogs(): void {
        this.logs.set([]);
        this.lastResultSuccess.set(undefined);
    }

    /**
     * Builds a Measure payload (parented under the Measure auto-store) for a
     * single {@link SoftwareMeasureDef}. The measure's `logFieldsMeta` mirror the
     * def's log columns.
     */
    private buildSoftwareMeasurePayload(
        measureType: IBizEntityType,
        measureParentId: string,
        def: SoftwareMeasureDef,
    ): IMeasure {
        const logFieldsMeta: IFieldMeta[] = def.fields.map(f => ({
            name: f.name,
            label: f.label,
            type: f.type,
            viewInList: true,
            isRequired: false,
        }));
        return {
            type: measureType,
            id: '1',
            parentId: measureParentId,
            tenantId: '1',
            appId: '1',
            name: def.name,
            shortName: def.shortName,
            description: `${def.name} — seeded by the ${SOFTWARE_MEASURE_PACK_NAME}.`,
            icon: 'cilChartLine',
            ownerId: Number(this.userContextService.userUUID),
            draft: false,
            additionalInfo: [],
            archived: false,
            archivedBy: null,
            archivedOn: null,
            createdOn: new Date(),
            createdBy: null,
            modifiedOn: null,
            modifiedBy: null,
            teamIds: [],
            progressData: null,
            labels: [],
            status: [],
            engagement: [],
            group: 'Retrospective',
            subGroup: 'Tickets',
            source: {
                connection: {
                    id: crypto.randomUUID(),
                    tenantId: '1',
                    appId: '1',
                    source: 1,
                    name: 'Manual',
                    connectionConfig: {},
                    pollingMode: 0,
                    pollingFrequencySeconds: 300,
                },
                source: 1,
                apiEndpoint: '',
            },
            category: def.category,
            logFieldsMeta,
        } as unknown as IMeasure;
    }

    /**
     * Builds the Measure Logs for one created measure, rebasing its series so the
     * earliest row lands on `startMs` (T) and later rows keep their original cadence
     * (T + n days). Every date-typed field value is shifted by the same per-measure
     * delta so columns stay consistent with the log date. Rows whose rebased primary
     * date exceeds `capMs` (End date, hard-capped at today) are dropped.
     */
    private buildSoftwareMeasureLogs(
        def: SoftwareMeasureDef,
        createdMeasure: IMeasure,
        startMs: number,
        capMs: number,
    ): ItxnRelationshipLog[] {
        // Prefer the persisted field meta (matched by name); fall back to the def's.
        const fieldMetaByName = new Map<string, IFieldMeta>();
        for (const f of def.fields) {
            const persisted = createdMeasure.logFieldsMeta?.find(m => m.name === f.name);
            fieldMetaByName.set(f.name, persisted ?? { name: f.name, label: f.label, type: f.type, viewInList: true, isRequired: false });
        }

        // Per-measure shift: earliest original log's day → the Start date's day.
        const earliestMs = Math.min(...def.rows.map(r => new Date(String(r[def.primaryDateLabel])).getTime()));
        const deltaMs = startOfDayMs(startMs) - startOfDayMs(earliestMs);

        const dateFieldLabels = new Set(def.fields.filter(f => f.type === FieldTypes.date).map(f => f.label));

        const logs: ItxnRelationshipLog[] = [];
        for (const row of def.rows) {
            const shiftedPrimary = new Date(new Date(String(row[def.primaryDateLabel])).getTime() + deltaMs);
            if (shiftedPrimary.getTime() > capMs) continue;

            const logFieldValues: IAdditionalInfo[] = def.fields.map(f => {
                let value = row[f.label];
                if (dateFieldLabels.has(f.label) && value != null && !Number.isNaN(new Date(String(value)).getTime())) {
                    value = formatDateLikeOriginal(String(value), new Date(new Date(String(value)).getTime() + deltaMs));
                }
                return { key: f.name, value, fieldMeta: fieldMetaByName.get(f.name)! };
            });

            logs.push({
                date: shiftedPrimary,
                measure: createdMeasure,
                logFieldValues,
                isActive: true,
                draft: false,
                deleted: false,
                archived: false,
            } as unknown as ItxnRelationshipLog);
        }
        return logs;
    }

    /**
     * Generates the "33 Software Measure Pack" — creates each measure in
     * {@link SOFTWARE_MEASURE_DEFS} under the Measure auto-store and seeds its
     * measure logs, rebased to the chosen Start date (T + n) and capped at the
     * End date / today.
     *
     * Efficient by design: one biz-entity-type lookup, one biz-entity lookup for
     * the parent, one Measure create per measure, one Measure list refresh to
     * resolve them, then all logs sent in throttled batches. Non-destructive.
     */
    async generateSoftwareMeasurePack(): Promise<void> {
        if (this.isGeneratingSoftwareMeasurePack()) return;
        this.isGeneratingSoftwareMeasurePack.set(true);
        this.lastResultSuccess.set(undefined);

        try {
            // Check if any entity creation is enabled
            if (!this.swPackCreateMeasureEffective() && !this.swPackCreateMeasureLogEffective()) {
                this.addLog('error', '❌ No entity creation options selected. Enable at least \"Create Measures\" or \"Create Measure Logs\".');
                this.lastResultSuccess.set(false);
                return;
            }

            // Resolve the Start date (T) and the cap (min of End date and today).
            const startMs = new Date(this.swPackStartDate()).getTime();
            const endMs = new Date(this.swPackEndDate()).getTime();
            if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
                this.addLog('error', '❌ Invalid Start/End date — please pick valid dates.');
                this.lastResultSuccess.set(false);
                return;
            }
            if (startMs > endMs) {
                this.addLog('error', '❌ Start date must be on or before the End date.');
                this.lastResultSuccess.set(false);
                return;
            }
            const capMs = Math.min(endOfDayMs(endMs), endOfDayMs(Date.now()));

            this.addLog('info', `Generating ${SOFTWARE_MEASURE_PACK_NAME} — ${SOFTWARE_MEASURE_DEFS.length} measures | T=${this.swPackStartDate()} → ${toISODate(new Date(capMs))}…`);

            // 1. Resolve ALL entity types in one call.
            const allTypes = await this.bizEntityTypeStateStoreService.getState();
            const measureType     = allTypes?.find(t => t.category === BizEntityTypeCategory.BenzaitenMeasure);
            const kpiType         = allTypes?.find(t => t.category === BizEntityTypeCategory.BenzaitenKPI);
            const deliverableType = allTypes?.find(t => t.category === BizEntityTypeCategory.BenzaitenDeliverable);
            const objectiveType   = allTypes?.find(t => t.category === BizEntityTypeCategory.BenzaitenObjective);
            const structuralType  = allTypes?.find(t => t.category === BizEntityTypeCategory.Structural);
            if (!measureType || !kpiType || !deliverableType || !objectiveType) {
                this.addLog('error', '❌ Required entity types not found — aborting.');
                this.lastResultSuccess.set(false);
                return;
            }

            // 2. Resolve auto-store parents in one call.
            const allEntities = await this.bizEntityStateStoreService.getState(this.bizEntityTypeStateStoreService);
            const measureParentId = allEntities
                ?.find(e => e.type?.category === BizEntityTypeCategory.BenzaitenMeasureAutoStore)
                ?.id?.toString();
            const kpiParentId = allEntities
                ?.find(e => e.type?.category === BizEntityTypeCategory.BenzaitenKPIAutoStore)
                ?.id?.toString();
            if (!measureParentId || !kpiParentId) {
                this.addLog('error', '❌ Measure or KPI auto-store not found — aborting.');
                this.lastResultSuccess.set(false);
                return;
            }

            // 3. Objective parent = the ApplicationRootNode entity — always present, no extra GET.
            const objectiveParentId = allEntities
                ?.find(e => e.type?.category === BizEntityTypeCategory.ApplicationRootNode)
                ?.id?.toString();
            if (!objectiveParentId) {
                this.addLog('error', '❌ Application root entity not found in allEntities — aborting.');
                this.lastResultSuccess.set(false);
                return;
            }

            // 3. Create every measure (one POST each) and collect the returned ids.
            const createdByDef: Array<{ def: SoftwareMeasureDef; measureId: string }> = [];
            if (this.swPackCreateMeasureEffective()) {
                for (const def of SOFTWARE_MEASURE_DEFS) {
                    const payload = this.buildSoftwareMeasurePayload(measureType, measureParentId, def);
                    const response = await this.measuresStateStoreService.create(payload);
                    const measureId = (response as unknown as { biz_entity_id?: string })?.biz_entity_id;
                    if (!measureId) {
                        this.addLog('warning', `⚠️ Measure create failed for '${def.name}' — response: ${JSON.stringify(response)}`);
                        continue;
                    }
                    createdByDef.push({ def, measureId });
                    await new Promise<void>(resolve => setTimeout(resolve, ENTITY_CREATE_GAP_MS));
                }
            }

            if (createdByDef.length === 0 && this.swPackCreateMeasureEffective()) {
                this.addLog('error', '❌ No measures were created — aborting.');
                this.lastResultSuccess.set(false);
                return;
            }

            // 4. Single Measure list refresh — resolves persisted measures for all defs.
            const measures = await this.measuresStateStoreService.getState(this.bizEntityTypeStateStoreService);
            const measureById = new Map<string, IMeasure>((measures ?? []).map((m: IMeasure) => [m.id!, m]));

            // 5. Build + send all measure logs BEFORE creating KPIs.
            const allLogs: ItxnRelationshipLog[] = [];
            if (this.swPackCreateMeasureLogEffective()) {
                for (const { def, measureId } of createdByDef) {
                    const createdMeasure = measureById.get(measureId);
                    if (!createdMeasure) {
                        this.addLog('warning', `⚠️ Could not resolve '${def.name}' after creation — logs skipped.`);
                        continue;
                    }
                    const logs = this.buildSoftwareMeasureLogs(def, createdMeasure, startMs, capMs);
                    allLogs.push(...logs);
                    this.addLog('info', `✔ ${def.name} — ${logs.length}/${def.rows.length} logs queued.`);
                }
                await sendInBatches(
                    allLogs,
                    (log: ItxnRelationshipLog) => this.measureLogStateStoreService.create(log) ?? Promise.resolve(),
                    LOG_BATCH_SIZE,
                    LOG_BATCH_DELAY_MS,
                );
                this.addLog('info', `✔ ${allLogs.length} measure logs sent.`);
            }

            // 6. Create one KPI per measure, linked to the created measure.
            const validForDuration = new DateRange(new Date(this.swPackStartDate()), new Date(this.swPackEndDate()));
            let kpiCount = 0;
            const kpiCreatedByDefKey: Array<{ def: SoftwareMeasureDef; kpiId: string }> = [];
            if (this.swPackCreateKPIEffective()) {
                for (const { def, measureId } of createdByDef) {
                    const createdMeasure = measureById.get(measureId);
                    if (!createdMeasure) continue;
                    const kpiPayload = {
                        type: kpiType,
                        id: '1',
                        parentId: kpiParentId,
                        tenantId: '1',
                        appId: '1',
                        name: def.name,
                        shortName: def.shortName,
                        description: def.name,
                        icon: 'cilSpeedometer',
                        ownerId: Number(this.userContextService.userUUID),
                        draft: false,
                        additionalInfo: [],
                        archived: false, archivedBy: null, archivedOn: null,
                        createdOn: new Date(), createdBy: null, modifiedOn: null, modifiedBy: null,
                        teamIds: [], progressData: null, labels: [], status: [], engagement: [],
                        validForDuration,
                        measureId: [createdMeasure.id!],
                        projectedProgression: [],
                        bizRules: def.bizRules,
                        statusFieldName: def.statusFieldName,
                        category: KPIAndMeasureCategories.Progress,
                        measure: [createdMeasure],
                    } as unknown as IKPI;
                    const kpiResponse = await this.kpiStateStoreService.create(kpiPayload);
                    const kpiEntityId = (kpiResponse as unknown as { biz_entity_id?: string })?.biz_entity_id;
                    if (kpiEntityId) {
                        kpiCount++;
                        kpiCreatedByDefKey.push({ def, kpiId: kpiEntityId });
                    } else {
                        this.addLog('warning', `⚠️ KPI create failed for '${def.name}' — response: ${JSON.stringify(kpiResponse)}`);
                    }
                    await new Promise<void>(resolve => setTimeout(resolve, ENTITY_CREATE_GAP_MS));
                }
            }

            // 6b. ONE getState — resolve all created KPIs with their measures (needed for Phase 2).
            const allKPIs = await this.kpiStateStoreService.getState(this.bizEntityTypeStateStoreService, this.measuresStateStoreService);
            const kpiByDefKey = new Map<string, IKPI>();
            for (const { def, kpiId } of kpiCreatedByDefKey) {
                const kpi = allKPIs?.find(k => k.id === kpiId);
                if (kpi) kpiByDefKey.set(def.key, kpi);
            }

            this.addLog('success', `✅ Done — ${createdByDef.length} measures, ${allLogs.length} logs, ${kpiCount} KPIs created.`);

            // Phase 2: objectives → deliverables → plans (all pre-resolved, zero extra GETs).
            if (this.swPackCreateDeliverableEffective()) {
                await this.generateSoftwareMeasurePackDeliverables(kpiByDefKey, deliverableType, objectiveType, objectiveParentId, structuralType);
            }

        } catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            this.addLog('error', `❌ Failed to generate ${SOFTWARE_MEASURE_PACK_NAME}: ${message}`);
            this.lastResultSuccess.set(false);
        } finally {
            this.isGeneratingSoftwareMeasurePack.set(false);
        }
    }

    /**
     * Phase 2 of the Software Measure Pack generation.
     *
     * Creates one Objective per KPI (33 total), each with 3 quarterly Deliverables
     * + Plans. The KPIs are the ones created in Phase 1 from {@link SOFTWARE_MEASURE_DEFS}
     * (with proper bizRules + statusFieldName), passed in via `kpiByDefKey`.
     *
     * For the 28 deliverable seeds that don't match a Phase 1 measure key, deliverables
     * are skipped gracefully (those seeds are for KPIs not yet in the pack data file).
     */
    private async generateSoftwareMeasurePackDeliverables(
        kpiByDefKey: Map<string, IKPI>,
        deliverableType: IBizEntityType,
        objectiveType: IBizEntityType,
        objectiveParentId: string,
        structuralType: IBizEntityType | undefined,
    ): Promise<void> {
        this.addLog('info', `Creating objectives + deliverables for ${kpiByDefKey.size} KPIs (${SOFTWARE_MEASURE_DELIVERABLE_DEFS.length} deliverables total)…`);

        const baseDate = new Date(SOFTWARE_MEASURE_DATA_START);
        const ownerId = Number(this.userContextService.userUUID);

        // Create logical department grouping entities so objectives live under a domain container
        // instead of directly under the application root.
        const deptIdBySeedKey = new Map<string, string>();
        if (structuralType) {
            this.addLog('info', `Creating ${SW_PACK_DEPT_GROUPS.length} department grouping entities…`);
            for (const group of SW_PACK_DEPT_GROUPS) {
                const deptResponse = await this.bizEntityStateStoreService.create({
                    type: structuralType,
                    id: '1',
                    parentId: objectiveParentId,
                    name: group.name,
                    shortName: group.shortName,
                    description: `${group.name} — ${SOFTWARE_MEASURE_PACK_NAME}`,
                    icon: group.icon,
                    ownerId,
                    draft: false,
                    additionalInfo: [],
                    archived: false, archivedBy: null, archivedOn: null,
                    createdOn: new Date(), createdBy: null, modifiedOn: null, modifiedBy: null,
                    teamIds: [], progressData: null, labels: [], status: [], engagement: [],
                } as unknown as IBizEntity);
                const deptId = (deptResponse as unknown as { biz_entity_id?: string })?.biz_entity_id;
                if (deptId) {
                    for (const key of group.keys) {
                        deptIdBySeedKey.set(key, deptId);
                    }
                    this.addLog('info', `✔ Department '${group.name}' created (${group.keys.length} KPIs).`);
                } else {
                    this.addLog('warning', `⚠️ Department create failed for '${group.name}' — its objectives will use the root.`);
                }
                await new Promise<void>(resolve => setTimeout(resolve, ENTITY_CREATE_GAP_MS));
            }
        }

        // All entity types + objective parent already resolved by Phase 1 — zero extra GETs here.
        const byKey = new Map<string, SoftwareMeasureDeliverableDef[]>();
        for (const def of SOFTWARE_MEASURE_DELIVERABLE_DEFS) {
            const arr = byKey.get(def.seedKey) ?? [];
            arr.push(def);
            byKey.set(def.seedKey, arr);
        }

        let objectivesCreated = 0;
        let deliverablesCreated = 0;
        const DAILY_SECONDS = 86_400;

        for (const [seedKey, quarterDefs] of byKey) {
            const kpi     = kpiByDefKey.get(seedKey);
            const kpiName = quarterDefs[0].kpiName;

            if (!kpi) {
                this.addLog('warning', `⚠️ KPI '${kpiName}' not in Phase 1 results. Skipping deliverables.`);
                continue;
            }
            const unit = kpi.unit ?? Units.find(u => u.id === quarterDefs[0].unitId)!;

            // One Objective per KPI — parented under its domain department (or root as fallback).
            const objStart = this.parseTDate(quarterDefs[0].startDate, baseDate);
            const objEnd   = this.parseTDate(quarterDefs[quarterDefs.length - 1].endDate, baseDate);
            const objectiveResponse = await this.objectiveStateStore.create({
                type: objectiveType,
                id: '1',
                parentId: deptIdBySeedKey.get(seedKey) ?? objectiveParentId,
                name: `Software Metrics: ${kpiName}`,
                shortName: kpiName,
                description: `${kpiName} — ${SOFTWARE_MEASURE_PACK_NAME}`,
                icon: 'cilFlagAlt',
                ownerId,
                draft: false,
                additionalInfo: [],
                archived: false, archivedBy: null, archivedOn: null,
                createdOn: new Date(), createdBy: null, modifiedOn: null, modifiedBy: null,
                teamIds: [], progressData: null, labels: [], status: [], engagement: [],
                duration: new DateRange(objStart, objEnd),
            } as unknown as IObjective);

            const objectiveId = (objectiveResponse as unknown as { biz_entity_id?: string })?.biz_entity_id;
            if (!objectiveId) {
                this.addLog('warning', `⚠️ Objective create failed for '${kpiName}'. Skipping.`);
                continue;
            }
            objectivesCreated++;

            // Three deliverables (Q1, Q2, Q3) — all linked to the same Phase 1 KPI
            for (const def of quarterDefs) {
                const delStart = this.parseTDate(def.startDate, baseDate);
                const delEnd   = this.parseTDate(def.endDate, baseDate);
                const duration = new DateRange(delStart, delEnd);
                const value    = new ValueRange(def.startVal, def.endVal);

                const deliverableResponse = await this.deliverableStateStoreService.create({
                    type: deliverableType,
                    id: '1',
                    parentId: objectiveId,
                    name: def.deliverableName,
                    shortName: def.deliverableName,
                    description: `${def.deliverableName} — ${SOFTWARE_MEASURE_PACK_NAME}`,
                    icon: 'cilTask',
                    ownerId,
                    draft: false,
                    additionalInfo: [],
                    archived: false, archivedBy: null, archivedOn: null,
                    createdOn: new Date(), createdBy: null, modifiedOn: null, modifiedBy: null,
                    teamIds: [], progressData: null, labels: [], status: [], engagement: [],
                    duration, value, unit,
                    kpis: [kpi],
                } as unknown as IDeliverable);

                const deliverableId = deliverableResponse?.biz_entity_id;
                if (!deliverableId) {
                    this.addLog('warning', `⚠️ Deliverable create failed for '${def.deliverableName}'.`);
                    continue;
                }
                deliverablesCreated++;

                const planItems = buildLinearPlanItems(def.startVal, def.endVal, delStart, delEnd, DAILY_SECONDS, unit);
                await this.planStateStoreService.create({
                    cadence: DAILY_SECONDS,
                    tolerance: 10,
                    deliverableId,
                    planItems,
                });

                await new Promise<void>(resolve => setTimeout(resolve, DELIVERABLE_GAP_MS));
            }

            this.addLog('info', `✔ ${kpiName} — objective + 3 deliverables + plans.`);
        }

        this.addLog('success', `✅ Done — ${objectivesCreated} objectives, ${deliverablesCreated}/${SOFTWARE_MEASURE_DELIVERABLE_DEFS.length} deliverables + plans.`);
        this.lastResultSuccess.set(true);
    }
    /** Parses a "T+N" expression into an absolute Date, where T = baseDate. */
    private parseTDate(tExpression: string, baseDate: Date): Date {
        const match = tExpression.match(/^T\+(\d+)$/);
        const days = match ? parseInt(match[1], 10) : 0;
        return new Date(baseDate.getTime() + days * 86_400_000);
    }

    private getSelectedUsersSignal(team: 'admin' | 'basic') {
        if (team === 'admin') return this.selectedAdminUserNames;
        return this.selectedBasicTeamUserNames;
    }

    isTeamUserSelected(team: 'admin' | 'basic', userName: string): boolean {
        return this.getSelectedUsersSignal(team)().has(userName);
    }

    toggleTeamUser(team: 'admin' | 'basic', userName: string): void {
        this.getSelectedUsersSignal(team).update(set => {
            const next = new Set(set);
            next.has(userName) ? next.delete(userName) : next.add(userName);
            return next;
        });
    }

    selectAllTeamUsers(team: 'admin' | 'basic'): void {
        const allNames = new Set(this.availableUsers().map(u => u.name ?? '').filter(Boolean));
        this.getSelectedUsersSignal(team).set(allNames);
    }

    deselectAllTeamUsers(team: 'admin' | 'basic'): void {
        this.getSelectedUsersSignal(team).set(new Set());
    }

    selectedTeamUserCount(team: 'admin' | 'basic'): number {
        return this.getSelectedUsersSignal(team)().size;
    }

    async seedOnly(): Promise<void> {
        if (this.isSeedingOnly() || !this.confirmSeed()) return;
        this.isSeedingOnly.set(true);
        this.seedResult.set(null);
        try {
            const skipUsersFromAdminTeam = this.availableUsers()
                .map(u => u.name ?? '')
                .filter(name => name && !this.selectedAdminUserNames().has(name));
            const skipUsersFromBasicTeam = this.availableUsers()
                .map(u => u.name ?? '')
                .filter(name => name && !this.selectedBasicTeamUserNames().has(name));
            this.svc.setConfig({
                skipUsersFromAdminTeam,
                skipUsersFromBasicTeam,
            });
            const result = await this.svc.cleanAndCreateTestData();
            this.seedResult.set({ success: result.success, error: result.error });
            this.confirmSeed.set(false);
        } finally {
            this.isSeedingOnly.set(false);
        }
    }

    async generate(): Promise<void> {
        const packId = this.selectedPackId();
        if (!packId || this.isGenerating()) return;

        this.isGenerating.set(true);
        this.logs.set([]);
        this.lastResultSuccess.set(undefined);
        const { start, end } = this.resolvedRange();
        this.addLog('info', `Starting — pack: ${this.selectedPack()?.displayName ?? packId} | window: ${start} → ${end}`);

        // Configure team memberships
        const skipUsersFromAdminTeam = this.availableUsers()
            .map(u => u.name ?? '')
            .filter(name => name && !this.selectedAdminUserNames().has(name));
        const skipUsersFromBasicTeam = this.availableUsers()
            .map(u => u.name ?? '')
            .filter(name => name && !this.selectedBasicTeamUserNames().has(name));
        this.svc.setConfig({
            skipUsersFromAdminTeam,
            skipUsersFromBasicTeam,
        });

        // predefined-kpis is non-destructive — runs directly without clean/wipe
        if (packId === 'predefined-kpis') {
            try {
                await this.generatePredefinedKpisMeasures();
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : String(err);
                this.addLog('error', `❌ Unexpected error: ${message}`);
                this.lastResultSuccess.set(false);
            } finally {
                this.isGenerating.set(false);
            }
            return;
        }

        // Register domain-specific entity creators — these are called by the
        // framework instead of the generic bizEntityStore.create() when the
        // entity type name matches, so app state-store services (e.g.
        // ObjectiveStateStoreService) are used for proper API routing and
        // the domain-specific shape (e.g. IObjective.duration) is applied.
        // Realistic pack uses exact KPI/Measure definitions with specific bizRules
        // instead of auto-generated generic ones.
        const isRealistic = packId === 'realistic';
        const isDense = packId === 'dense' || isRealistic;
        const deliverableKPICategories: KPIAndMeasureCategories[] = isDense
            ? [
                KPIAndMeasureCategories.Progress,
                KPIAndMeasureCategories.Performance,
                KPIAndMeasureCategories.Quality,
                KPIAndMeasureCategories.Innovation,
              ]
            : [KPIAndMeasureCategories.Progress];
        this.svc.setConfig({
            customEntityCreators: {
                'Objective': this.buildObjectiveCreator(),
                'Deliverable': isRealistic
                    ? this.buildRealisticDeliverableCreator()
                    : this.buildDeliverableCreator(deliverableKPICategories, isDense),
            },
        });
        console.log('[BenzaitenPanel] setConfig(customEntityCreators) called — about to cleanAndCreateWithTestPack');

        try {
            // Cast needed: local multi-config types aren't in the installed package yet.
            const result = await this.svc.cleanAndCreateWithTestPack(
                packId,
                this.packConfig() as unknown as Parameters<typeof this.svc.cleanAndCreateWithTestPack>[1]
            );

            if (result.success) {
                this.addLog('success', '✅ Generation complete — all entities and relationships created successfully.');
                this.lastResultSuccess.set(true);
            } else {
                const msg = result.error ?? 'Unknown error';
                this.addLog('error', `❌ Generation failed: ${msg}`);
                if (!result.seedSuccess) {
                    this.addLog('warning', 'Seed phase failed — no data was created.');
                } else if (!result.testSuccess) {
                    this.addLog('warning', 'Test pack phase failed — seed data is intact.');
                }
                this.lastResultSuccess.set(false);
            }
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : String(err);
            this.addLog('error', `❌ Unexpected error: ${message}`);
            this.lastResultSuccess.set(false);
        } finally {
            this.isGenerating.set(false);
        }
    }

    private addLog(type: LogEntry['type'], message: string): void {
        const timestamp = new Date().toLocaleTimeString();
        this.logs.update(l => [...l, { type, message, timestamp }]);
    }

    /**
     * Builds a custom entity creator for 'Objective' entities.
     *
     * Closes over the currently resolved date range so every Objective created
     * during this run receives the correct `duration` without the entity factory
     * (which lives in the framework data file) needing to know about DateRange.
     */
    private buildObjectiveCreator(): (entityData: Partial<IBizEntity>) => Promise<{ biz_entity_id?: string; id?: string } | undefined> {
        const { start, end } = resolveDateRange(this.dateRangeConfig());
        const duration = new DateRange(new Date(start), new Date(end));
        const stateStore = this.objectiveStateStore;
        console.log('[BenzaitenPanel] buildObjectiveCreator called — creator registered for "Objective"');
        return async (entityData: Partial<IBizEntity>) => {
            console.log('[BenzaitenPanel] Objective custom creator INVOKED for:', entityData?.name);
            const objective = {
                ...entityData,
                duration,
                status: [],
                engagement: [],
            } as IObjective;
            return stateStore.create(objective);
        };
    }

    /**
     * Builds a custom entity creator for 'Deliverable' entities.
     *
     * Each invocation:
     *   1. Creates one Measure per KPI category via AutoCreateEntitiesService
     *      (Measure → KPI chain, parented under auto-created stores).
     *   2. Creates the Deliverable with all created KPIs attached.
     *   3. Creates a monthly linear Plan from the deliverable's duration and
     *      value range.
     *
     * @param categories  KPI categories to create per deliverable (Progress only
     *                    for smoke; all 4 for dense).
     * @param withUnitVariation  When true, cycles deliverables through Percent,
     *                           Unit, Thousand and US Dollars for dense-pack
     *                           variety. When false, always uses Percent.
     */
    private buildDeliverableCreator(
        categories: KPIAndMeasureCategories[],
        withUnitVariation: boolean,
    ): (entityData: Partial<IBizEntity>) => Promise<{ biz_entity_id?: string; id?: string } | undefined> {
        const { start, end } = resolveDateRange(this.dateRangeConfig());
        const duration = new DateRange(new Date(start), new Date(end));

        // Unit cycling: Percent → Unit (count) → Thousand → US Dollars
        const UNIT_VARIATIONS: Array<{ unitId: string; startVal: number; endVal: number }> = [
            { unitId: '25', startVal: 0, endVal: 100 },   // Percent %
            { unitId: '20', startVal: 0, endVal: 50 },    // Unit (count)
            { unitId: '23', startVal: 0, endVal: 10 },    // Thousand
            { unitId: '36', startVal: 0, endVal: 500 },   // US Dollars $
        ];

        const bizEntityTypeStateSvc = this.bizEntityTypeStateStoreService;
        const bizEntityStateSvc = this.bizEntityStateStoreService;
        const autoCreateSvc = this.autoCreateEntitiesService;
        const kpiStateSvc = this.kpiStateStoreService;
        const measureStateSvc = this.measuresStateStoreService;
        const deliverableStateSvc = this.deliverableStateStoreService;
        const planStateSvc = this.planStateStoreService;
        const measureLogStateSvc = this.measureLogStateStoreService;
        let deliverableIndex = 0;

        return async (entityData: Partial<IBizEntity>) => {
            const idx = deliverableIndex++;
            console.log(`[BenzaitenPanel] Deliverable creator INVOKED for: ${entityData?.name} (index ${idx})`);

            // 1. Resolve unit + value range for this deliverable
            const variation = withUnitVariation
                ? UNIT_VARIATIONS[idx % UNIT_VARIATIONS.length]
                : UNIT_VARIATIONS[0]; // always Percent for non-dense packs
            const unit = Units.find(u => u.id === variation.unitId)!;
            const value = new ValueRange(variation.startVal, variation.endVal);

            // 2. Look up KPI and Measure entity types
            const allTypes = await bizEntityTypeStateSvc.getState();
            console.log(`[BenzaitenPanel] allTypes count: ${allTypes?.length} | BenzaitenKPI=${BizEntityTypeCategory.BenzaitenKPI} | BenzaitenMeasure=${BizEntityTypeCategory.BenzaitenMeasure}`);
            const kpiType = allTypes?.find(t => t.category === BizEntityTypeCategory.BenzaitenKPI);
            const measureType = allTypes?.find(t => t.category === BizEntityTypeCategory.BenzaitenMeasure);
            if (!kpiType || !measureType) {
                throw new Error(`[BenzaitenPanel] KPI or Measure entity type not found. kpiType=${kpiType?.name} measureType=${measureType?.name}`);
            }

            // 3. Look up auto store parent IDs (created during seed phase).
            //    Must pass bizEntityTypeStateSvc so entities have type.category populated.
            const allEntities = await bizEntityStateSvc.getState(bizEntityTypeStateSvc);
            console.log(`[BenzaitenPanel] allEntities count: ${allEntities?.length}`);
            const kpiParentId = allEntities
                ?.find(e => e.type?.category === BizEntityTypeCategory.BenzaitenKPIAutoStore)
                ?.id?.toString();
            const measureParentId = allEntities
                ?.find(e => e.type?.category === BizEntityTypeCategory.BenzaitenMeasureAutoStore)
                ?.id?.toString();
            console.log(`[BenzaitenPanel] kpiParentId=${kpiParentId} | measureParentId=${measureParentId}`);
            if (!kpiParentId || !measureParentId) {
                const categories_found = allEntities?.map(e => e.type?.category).join(',');
                throw new Error(`[BenzaitenPanel] KPI or Measure auto store parent not found. Entity categories found: ${categories_found}`);
            }

            // 4. Create all KPIs+Measures (one per category). Each call internally
            //    creates a Measure, so these must remain sequential.
            const createdKPIs: IKPI[] = [];
            const kpiIds: string[] = [];
            for (const category of categories) {
                // createKPIAutomatically is typed as Promise<IKPI> but resolves
                // to the raw API response { biz_entity_id: string }.
                const kpiResponse = await autoCreateSvc.createKPIAutomatically(
                    kpiType, measureType,
                    kpiParentId, measureParentId,
                    entityData.name ?? `Deliverable ${idx + 1}`,
                    entityData.ownerId ?? '',
                    category,
                ) as unknown as { biz_entity_id?: string };
                if (kpiResponse?.biz_entity_id) {
                    kpiIds.push(kpiResponse.biz_entity_id);
                }
            }

            // 4b. Single getState() call — populates all KPIs with their Measures.
            //     Previously this was called once per category; a single call is enough.
            const allKPIs = kpiIds.length > 0
                ? await kpiStateSvc.getState(bizEntityTypeStateSvc, measureStateSvc)
                : [];

            // 4c. Build all log payloads in memory (CPU only, no network requests).
            const logDates = buildLogDates(new Date(start), new Date());
            const allLogs: ItxnRelationshipLog[] = [];
            for (const kpiId of kpiIds) {
                const kpi = allKPIs?.find(k => k.id === kpiId);
                if (!kpi) continue;
                createdKPIs.push(kpi);
                const measure = kpi.measure?.[0];
                if (!measure) continue;
                const fieldsMeta = measure.logFieldsMeta?.length
                    ? measure.logFieldsMeta
                    : [{ name: 'status', label: 'Status', type: FieldTypes.number, isRequired: true }];
                // Stateful random-walk series — correlated values, realistic variance.
                const series = buildNoisySeries(logDates.length, variation.startVal, variation.endVal);
                for (let li = 0; li < logDates.length; li++) {
                    const logValue = series[li];
                    const logFieldValues: IAdditionalInfo[] = fieldsMeta.map(field => ({
                        key: field.name,
                        value: field.type === FieldTypes.number ? logValue : String(logValue),
                        fieldMeta: field,
                    }));
                    allLogs.push({
                        date: logDates[li],
                        measure,
                        logFieldValues,
                        isActive: true,
                        draft: false,
                        deleted: false,
                        archived: false,
                    } as unknown as ItxnRelationshipLog);
                }
            }

            // 4d. Rate-limited batch send: LOG_BATCH_SIZE concurrent requests,
            //     LOG_BATCH_DELAY_MS pause between batches.
            await sendInBatches(
                allLogs,
                (log: ItxnRelationshipLog) => measureLogStateSvc.create(log) ?? Promise.resolve(),
                LOG_BATCH_SIZE,
                LOG_BATCH_DELAY_MS,
            );

            // 5. Create the Deliverable with all KPIs attached
            const deliverable = {
                ...entityData,
                duration,
                value,
                unit,
                kpis: createdKPIs,
                status: [],
                engagement: [],
            } as IDeliverable;
            const deliverableResponse = await deliverableStateSvc.create(deliverable);

            // 6. Create a monthly linear Plan aligned to deliverable duration
            if (deliverableResponse?.biz_entity_id) {
                const MONTHLY_SECONDS = 86_400 * 30;
                const planItems = buildLinearPlanItems(
                    variation.startVal, variation.endVal,
                    new Date(start), new Date(end),
                    MONTHLY_SECONDS, unit,
                );
                await planStateSvc.create({
                    cadence: MONTHLY_SECONDS,
                    tolerance: 10,
                    deliverableId: deliverableResponse.biz_entity_id,
                    planItems,
                });
            }

            // Courtesy pause — gives the backend a breath before the next deliverable.\n            await new Promise<void>(resolve => setTimeout(resolve, DELIVERABLE_GAP_MS));

            return deliverableResponse;
        };
    }

    /**
     * Creates all predefined KPIs and Measures from REALISTIC_DELIVERABLE_DEFS
     * as standalone entities under the auto stores — without touching any
     * Deliverable hierarchy. Non-destructive: no clean/wipe is performed.
     */
    private async generatePredefinedKpisMeasures(): Promise<void> {
        const { start } = resolveDateRange(this.dateRangeConfig());

        const bizEntityTypeStateSvc = this.bizEntityTypeStateStoreService;
        const bizEntityStateSvc = this.bizEntityStateStoreService;
        const kpiStateSvc = this.kpiStateStoreService;
        const measureStateSvc = this.measuresStateStoreService;
        const measureLogStateSvc = this.measureLogStateStoreService;

        // Resolve entity types
        const allTypes = await bizEntityTypeStateSvc.getState();
        const kpiType = allTypes?.find(t => t.category === BizEntityTypeCategory.BenzaitenKPI);
        const measureType = allTypes?.find(t => t.category === BizEntityTypeCategory.BenzaitenMeasure);
        if (!kpiType || !measureType) {
            this.addLog('error', '❌ KPI or Measure entity type not found — aborting.');
            this.lastResultSuccess.set(false);
            return;
        }

        // Resolve auto-store parent IDs
        const allEntities = await bizEntityStateSvc.getState(bizEntityTypeStateSvc);
        const kpiParentId = allEntities
            ?.find(e => e.type?.category === BizEntityTypeCategory.BenzaitenKPIAutoStore)
            ?.id?.toString();
        const measureParentId = allEntities
            ?.find(e => e.type?.category === BizEntityTypeCategory.BenzaitenMeasureAutoStore)
            ?.id?.toString();
        if (!kpiParentId || !measureParentId) {
            this.addLog('error', '❌ KPI or Measure auto store not found — aborting.');
            this.lastResultSuccess.set(false);
            return;
        }

        const defEntries = Object.entries(REALISTIC_DELIVERABLE_DEFS);
        this.addLog('info', `Creating KPIs and Measures for ${defEntries.length} deliverable definitions…`);

        let kpiCount = 0;
        let measureCount = 0;
        for (const [defKey, deliverableDef] of defEntries) {
            for (const kpiDef of deliverableDef.kpis) {
                // Create Measure
                const measurePayload: IMeasure = {
                    type: measureType,
                    id: '1',
                    parentId: measureParentId,
                    tenantId: '1',
                    appId: '1',
                    name: kpiDef.measure.name,
                    shortName: kpiDef.measure.name,
                    description: kpiDef.measure.name,
                    icon: 'cilChartLine',
                    ownerId: Number(this.userContextService.userUUID),
                    draft: false,
                    additionalInfo: [],
                    archived: false,
                    archivedBy: null,
                    archivedOn: null,
                    createdOn: new Date(),
                    createdBy: null,
                    modifiedOn: null,
                    modifiedBy: null,
                    teamIds: [],
                    progressData: null,
                    labels: [],
                    status: [],
                    engagement: [],
                    group: 'Retrospective',
                    subGroup: 'Tickets',
                    source: {
                        connection: {
                            id: crypto.randomUUID(),
                            tenantId: '1',
                            appId: '1',
                            source: 1,
                            name: 'Manual',
                            connectionConfig: {},
                            pollingMode: 0,
                            pollingFrequencySeconds: 300,
                        },
                        source: 1,
                        apiEndpoint: '',
                    },
                    category: kpiDef.measure.category,
                    logFieldsMeta: kpiDef.measure.logFieldsMeta,
                } as unknown as IMeasure;

                const measureResponse = await measureStateSvc.create(measurePayload);
                const measureResponseId = (measureResponse as unknown as { biz_entity_id?: string })?.biz_entity_id;
                if (!measureResponseId) {
                    this.addLog('warning', `⚠️ Measure create failed for '${kpiDef.measure.name}'`);
                    continue;
                }
                measureCount++;

                // Fetch freshly created measure
                const allMeasures = await measureStateSvc.getState();
                const createdMeasure = allMeasures?.find((m: IMeasure) => m.id === measureResponseId);
                if (!createdMeasure) {
                    this.addLog('warning', `⚠️ Could not resolve measure '${kpiDef.measure.name}' after creation`);
                    continue;
                }

                // Create KPI linked to the measure
                const minDate = new Date();
                minDate.setFullYear(minDate.getFullYear() - 1);
                const maxDate = new Date();
                maxDate.setFullYear(maxDate.getFullYear() + 1);

                const kpiPayload: IKPI = {
                    type: kpiType,
                    id: '1',
                    parentId: kpiParentId,
                    tenantId: '1',
                    appId: '1',
                    name: kpiDef.kpiName,
                    shortName: kpiDef.kpiName,
                    description: kpiDef.kpiName,
                    icon: 'cilSpeedometer',
                    ownerId: Number(this.userContextService.userUUID),
                    draft: false,
                    additionalInfo: [],
                    archived: false,
                    archivedBy: null,
                    archivedOn: null,
                    createdOn: new Date(),
                    createdBy: null,
                    modifiedOn: null,
                    modifiedBy: null,
                    teamIds: [],
                    progressData: null,
                    labels: [],
                    status: [],
                    engagement: [],
                    validForDuration: new DateRange(minDate, maxDate),
                    measureId: [createdMeasure.id!],
                    projectedProgression: [],
                    bizRules: kpiDef.bizRules,
                    statusFieldName: kpiDef.statusFieldName,
                    category: kpiDef.category,
                    measure: [createdMeasure],
                } as unknown as IKPI;

                const kpiResponse = await kpiStateSvc.create(kpiPayload);
                const kpiId = (kpiResponse as unknown as { biz_entity_id?: string })?.biz_entity_id;
                if (!kpiId) {
                    this.addLog('warning', `⚠️ KPI create failed for '${kpiDef.kpiName}'`);
                    continue;
                }
                kpiCount++;

                // Create logs for this measure
                const allKPIs = await kpiStateSvc.getState(bizEntityTypeStateSvc, measureStateSvc);
                const createdKPI = allKPIs?.find(k => k.id === kpiId);
                const linkedMeasure = createdKPI?.measure?.[0];
                if (linkedMeasure) {
                    const fieldsMeta = linkedMeasure.logFieldsMeta?.length
                        ? linkedMeasure.logFieldsMeta
                        : [{ name: 'status', label: 'Status', type: FieldTypes.number, isRequired: true }];
                    const logDates = buildLogDates(new Date(start), new Date());
                    const series = buildNoisySeries(logDates.length, deliverableDef.startVal, deliverableDef.endVal);
                    const primaryField = deliverableDef.logValueFieldName;
                    const logs: ItxnRelationshipLog[] = logDates.map((logDate, li) => ({
                        date: logDate,
                        measure: linkedMeasure,
                        logFieldValues: fieldsMeta.map((field: IFieldMeta) => {
                            let value: string | number;
                            if (field.name === primaryField) {
                                value = series[li];
                            } else if (field.type === FieldTypes.date) {
                                value = logDate.toISOString().split('T')[0];
                            } else if (field.type === FieldTypes.calculatedColumn) {
                                value = 0;
                            } else if (field.type === FieldTypes.number) {
                                if (field.name === 'month') value = logDate.getMonth() + 1;
                                else if (field.name === 'year') value = logDate.getFullYear();
                                else value = Math.max(1, Math.round(Math.random() * 50));
                            } else {
                                value = 'generated';
                            }
                            return { key: field.name, value, fieldMeta: field };
                        }),
                        isActive: true,
                        draft: false,
                        deleted: false,
                        archived: false,
                    } as unknown as ItxnRelationshipLog));
                    await sendInBatches(
                        logs,
                        (log: ItxnRelationshipLog) => measureLogStateSvc.create(log) ?? Promise.resolve(),
                        LOG_BATCH_SIZE,
                        LOG_BATCH_DELAY_MS,
                    );
                }
            }
            this.addLog('info', `✔ ${defKey} — KPIs and Measures created`);
        }

        this.addLog('success', `✅ Done — ${kpiCount} KPIs and ${measureCount} Measures created.`);
        this.lastResultSuccess.set(true);
    }

    /**
     * Builds a Deliverable creator for the realistic pack.
     *
     * Unlike `buildDeliverableCreator`, this creator does NOT call
     * `AutoCreateEntitiesService.createKPIAutomatically()`. Instead it creates
     * each Measure and KPI directly — with exact bizRules and logFieldsMeta
     * sourced from the live environment — using data keyed by the deliverable's
     * `localId` suffix (e.g. 'del-mrr-1').
     *
     * The deliverable's `additionalInfo` is expected to carry the pack localId
     * suffix so we can look it up in `REALISTIC_DELIVERABLE_DEFS`.
     */
    private buildRealisticDeliverableCreator(): (entityData: Partial<IBizEntity>) => Promise<{ biz_entity_id?: string; id?: string } | undefined> {
        const { start, end } = resolveDateRange(this.dateRangeConfig());
        const duration = new DateRange(new Date(start), new Date(end));

        const bizEntityTypeStateSvc = this.bizEntityTypeStateStoreService;
        const bizEntityStateSvc = this.bizEntityStateStoreService;
        const kpiStateSvc = this.kpiStateStoreService;
        const measureStateSvc = this.measuresStateStoreService;
        const deliverableStateSvc = this.deliverableStateStoreService;
        const planStateSvc = this.planStateStoreService;
        const measureLogStateSvc = this.measureLogStateStoreService;

        return async (entityData: Partial<IBizEntity>) => {
            // The framework passes entity.id as the localId: 'realistic:del-mrr-1'
            // Strip the pack scope prefix to get the def key: 'del-mrr-1'
            const entityLocalId = entityData.id ?? '';
            const defKey = entityLocalId.replace(/^realistic:/, '');
            const deliverableDef = REALISTIC_DELIVERABLE_DEFS[defKey];

            if (!deliverableDef) {
                console.warn(`[BenzaitenPanel] No realistic def found for key '${defKey}' (entityId='${entityLocalId}'). Skipping KPI creation.`);
                return undefined;
            }

            console.log(`[BenzaitenPanel] Realistic creator for '${defKey}': ${deliverableDef.kpis.length} KPI(s)`);

            // 1. Resolve entity types (KPI + Measure)
            const allTypes = await bizEntityTypeStateSvc.getState();
            const kpiType = allTypes?.find(t => t.category === BizEntityTypeCategory.BenzaitenKPI);
            const measureType = allTypes?.find(t => t.category === BizEntityTypeCategory.BenzaitenMeasure);
            if (!kpiType || !measureType) {
                throw new Error(`[BenzaitenPanel][realistic] KPI or Measure entity type not found.`);
            }

            // 2. Resolve auto-store parent IDs
            const allEntities = await bizEntityStateSvc.getState(bizEntityTypeStateSvc);
            const kpiParentId = allEntities
                ?.find(e => e.type?.category === BizEntityTypeCategory.BenzaitenKPIAutoStore)
                ?.id?.toString();
            const measureParentId = allEntities
                ?.find(e => e.type?.category === BizEntityTypeCategory.BenzaitenMeasureAutoStore)
                ?.id?.toString();
            if (!kpiParentId || !measureParentId) {
                throw new Error(`[BenzaitenPanel][realistic] KPI or Measure auto store parent not found.`);
            }

            // 3. Create each Measure → KPI sequentially, collecting KPI ids
            const kpiIds: string[] = [];
            for (const kpiDef of deliverableDef.kpis) {
                // 3a. Create Measure
                const measurePayload: IMeasure = {
                    type: measureType,
                    id: '1',
                    parentId: measureParentId,
                    tenantId: '1',
                    appId: '1',
                    name: kpiDef.measure.name,
                    shortName: kpiDef.measure.name,
                    description: kpiDef.measure.name,
                    icon: 'cilChartLine',
                    ownerId: Number(entityData.ownerId) || Number(this.userContextService.userUUID),
                    draft: false,
                    additionalInfo: [],
                    archived: false,
                    archivedBy: null,
                    archivedOn: null,
                    createdOn: new Date(),
                    createdBy: null,
                    modifiedOn: null,
                    modifiedBy: null,
                    teamIds: [],
                    progressData: null,
                    labels: [],
                    status: [],
                    engagement: [],
                    group: 'Retrospective',
                    subGroup: 'Tickets',
                    source: {
                        connection: {
                            id: crypto.randomUUID(),
                            tenantId: '1',
                            appId: '1',
                            source: 1, // MeasureSources.MANUAL
                            name: 'Manual',
                            connectionConfig: {},
                            pollingMode: 0, // ConnectionPollingModes.POLL
                            pollingFrequencySeconds: 300,
                        },
                        source: 1,
                        apiEndpoint: '',
                    },
                    category: kpiDef.measure.category,
                    logFieldsMeta: kpiDef.measure.logFieldsMeta,
                } as unknown as IMeasure;

                const measureResponse = await measureStateSvc.create(measurePayload);
                if (!measureResponse) {
                    console.error(`[BenzaitenPanel][realistic] Measure create failed for '${kpiDef.measure.name}'`);
                    continue;
                }
                const measureResponseId = (measureResponse as unknown as { biz_entity_id?: string })?.biz_entity_id;

                // 3b. Fetch fresh measure state to get server-assigned ID
                const allMeasures = await measureStateSvc.getState();
                const createdMeasure = allMeasures?.find(
                    (m: IMeasure) => m.id === measureResponseId
                );
                if (!createdMeasure) {
                    console.error(`[BenzaitenPanel][realistic] Could not resolve created measure for '${kpiDef.measure.name}'`);
                    continue;
                }

                // 3c. Create KPI linked to the measure
                const minDate = new Date();
                minDate.setFullYear(minDate.getFullYear() - 1);
                const maxDate = new Date();
                maxDate.setFullYear(maxDate.getFullYear() + 1);

                const kpiPayload: IKPI = {
                    type: kpiType,
                    id: '1',
                    parentId: kpiParentId,
                    tenantId: '1',
                    appId: '1',
                    name: kpiDef.kpiName,
                    shortName: kpiDef.kpiName,
                    description: kpiDef.kpiName,
                    icon: 'cilSpeedometer',
                    ownerId: Number(entityData.ownerId) || Number(this.userContextService.userUUID),
                    draft: false,
                    additionalInfo: [],
                    archived: false,
                    archivedBy: null,
                    archivedOn: null,
                    createdOn: new Date(),
                    createdBy: null,
                    modifiedOn: null,
                    modifiedBy: null,
                    teamIds: [],
                    progressData: null,
                    labels: [],
                    status: [],
                    engagement: [],
                    validForDuration: new DateRange(minDate, maxDate),
                    measureId: [createdMeasure.id!],
                    projectedProgression: [],
                    bizRules: kpiDef.bizRules,
                    statusFieldName: kpiDef.statusFieldName,
                    category: kpiDef.category,
                    measure: [createdMeasure],
                } as unknown as IKPI;

                const kpiResponse = await kpiStateSvc.create(kpiPayload);
                const kpiId = (kpiResponse as unknown as { biz_entity_id?: string })?.biz_entity_id;
                if (kpiId) {
                    kpiIds.push(kpiId);
                }
            }

            // 4. Fetch all KPIs to get populated measure references
            const allKPIs = kpiIds.length > 0
                ? await kpiStateSvc.getState(bizEntityTypeStateSvc, measureStateSvc)
                : [];

            const createdKPIs: IKPI[] = [];
            const logDates = buildLogDates(new Date(start), new Date());
            const allLogs: ItxnRelationshipLog[] = [];

            for (const kpiId of kpiIds) {
                const kpi = allKPIs?.find(k => k.id === kpiId);
                if (!kpi) continue;
                createdKPIs.push(kpi);
                const measure = kpi.measure?.[0];
                if (!measure) continue;

                // Use logFieldsMeta from the measure; fall back to a 'status' field
                const fieldsMeta = measure.logFieldsMeta?.length
                    ? measure.logFieldsMeta
                    : [{ name: 'status', label: 'Status', type: FieldTypes.number, isRequired: true }];

                // Noisy series for the primary metric field.
                // All other fields get type-appropriate independent values.
                const primaryField = deliverableDef.logValueFieldName;
                const series = buildNoisySeries(logDates.length, deliverableDef.startVal, deliverableDef.endVal);
                for (let li = 0; li < logDates.length; li++) {
                    const logValue = series[li];
                    const logDate = logDates[li];
                    const logFieldValues: IAdditionalInfo[] = fieldsMeta.map((field: IFieldMeta) => {
                        let value: string | number;
                        if (field.name === primaryField) {
                            // Primary metric — noisy series tracking startVal → endVal
                            value = logValue;
                        } else if (field.type === FieldTypes.date) {
                            // Date fields — use the log entry's own date
                            value = logDate.toISOString().split('T')[0];
                        } else if (field.type === FieldTypes.calculatedColumn) {
                            // Calculated fields — backend derives from raw inputs via bizRules; skip
                            value = 0;
                        } else if (field.type === FieldTypes.number) {
                            // Well-known calendar fields get meaningful values from the log date
                            if (field.name === 'month') {
                                value = logDate.getMonth() + 1;
                            } else if (field.name === 'year') {
                                value = logDate.getFullYear();
                            } else {
                                // Independent random number in a realistic range
                                value = Math.max(1, Math.round(Math.random() * 50));
                            }
                        } else {
                            // Text / other fields — generic placeholder
                            value = 'generated';
                        }
                        return { key: field.name, value, fieldMeta: field };
                    });
                    allLogs.push({
                        date: logDates[li],
                        measure,
                        logFieldValues,
                        isActive: true,
                        draft: false,
                        deleted: false,
                        archived: false,
                    } as unknown as ItxnRelationshipLog);
                }
            }

            // 5. Send logs in batches
            await sendInBatches(
                allLogs,
                (log: ItxnRelationshipLog) => measureLogStateSvc.create(log) ?? Promise.resolve(),
                LOG_BATCH_SIZE,
                LOG_BATCH_DELAY_MS,
            );

            // 6. Resolve unit from deliverable def
            const unit = Units.find(u => u.id === deliverableDef.unitId)!;
            const value = new ValueRange(deliverableDef.startVal, deliverableDef.endVal);

            // 7. Create the Deliverable with the exact KPIs attached
            const deliverable = {
                ...entityData,
                duration,
                value,
                unit,
                kpis: createdKPIs,
                status: [],
                engagement: [],
            } as IDeliverable;
            const deliverableResponse = await deliverableStateSvc.create(deliverable);

            // 8. Create a monthly linear Plan
            if (deliverableResponse?.biz_entity_id) {
                const MONTHLY_SECONDS = 86_400 * 30;
                const planItems = buildLinearPlanItems(
                    deliverableDef.startVal, deliverableDef.endVal,
                    new Date(start), new Date(end),
                    MONTHLY_SECONDS, unit,
                );
                await planStateSvc.create({
                    cadence: MONTHLY_SECONDS,
                    tolerance: 10,
                    deliverableId: deliverableResponse.biz_entity_id,
                    planItems,
                });
            }

            await new Promise<void>(resolve => setTimeout(resolve, DELIVERABLE_GAP_MS));

            return deliverableResponse;
        };
    }
}

/** Generates linear-interpolation plan items from startVal → endVal at cadence intervals. */
function buildLinearPlanItems(
    startVal: number,
    endVal: number,
    startDt: Date,
    endDt: Date,
    cadenceSeconds: number,
    unit: Unit,
): iPlanItems[] {
    const items: iPlanItems[] = [];
    const intervals: Date[] = [];
    let current = new Date(startDt);
    while (current <= endDt) {
        intervals.push(new Date(current));
        current = new Date(current.getTime() + cadenceSeconds * 1_000);
    }
    if (intervals.length === 0) return [];
    for (let i = 0; i < intervals.length; i++) {
        const progress = intervals.length === 1
            ? startVal
            : startVal + ((endVal - startVal) / (intervals.length - 1)) * i;
        items.push({
            id: crypto.randomUUID(),
            sortOrder: i + 1,
            date: intervals[i],
            expected: Math.round(progress * 100) / 100, // round to 2 decimal places
            unitId: unit.id,
            isManual: false,
            unit,
        });
    }
    return items;
}

/** Max concurrent log requests per batch — prevents overwhelming the backend. */
const LOG_BATCH_SIZE = 5;
/** Pause (ms) between log batches during bulk log generation. */
const LOG_BATCH_DELAY_MS = 300;
/** Pause (ms) between individual entity (Measure / KPI) POST calls to avoid 429s. */
const ENTITY_CREATE_GAP_MS = 150;
/**
 * Courtesy pause between each deliverable's full create cycle
 * (Measures → KPIs → Deliverable → Plan → Logs).
 * Gives the backend a breath between heavy write sequences.
 * Increase if you observe 429/503 responses at high deliverable counts.
 */
const DELIVERABLE_GAP_MS = 400;

/**
 * Logical department groupings for the 33 Software Measure Pack.
 *
 * Each group becomes one Structural (department) entity under the application root,
 * and all objectives for KPIs in that group are parented under it.
 */
const SW_PACK_DEPT_GROUPS: Array<{ name: string; shortName: string; icon: string; keys: string[] }> = [
    {
        name: 'Engineering Delivery',
        shortName: 'Eng Delivery',
        icon: 'cilSpeedometer',
        keys: [
            'sprint_velocity',
            'cycle_time',
            'lead_time_for_changes',
            'deployment_frequency',
            'change_failure_rate',
            'mean_time_to_recovery_mttr',
            'commit_to_deploy_time',
        ],
    },
    {
        name: 'Quality & Reliability',
        shortName: 'Quality',
        icon: 'cilShieldAlt',
        keys: [
            'code_coverage_delta',
            'bug_fix_rate',
            'defect_escape_rate',
            'technical_debt_ratio',
            'customer_reported_bugs',
            'module_level_technical_debt',
        ],
    },
    {
        name: 'Product & Feature Delivery',
        shortName: 'Product',
        icon: 'cilTask',
        keys: [
            'feature_completion_rate',
            'module_completion_percentage',
            'feature_development_cycle_time',
            'feature_testing_pass_rate',
            'bug_rate_per_feature',
            'feature_release_predictability',
            'module_integration_success_rate',
            'feature_rework_rate',
            'feature_readiness_index',
            'post_release_defect_rate_per_feature',
            'feature_adoption_rate',
            'roadmap_adherence_for_features',
            'feature_deployment_frequency',
        ],
    },
    {
        name: 'Process & Collaboration',
        shortName: 'Process',
        icon: 'cilPeople',
        keys: [
            'pull_request_merge_time',
            'work_in_progress_wip',
            'unplanned_work_rate',
            'github_issues_closure_rate',
            'github_pull_request_review_time',
            'github_contributor_activity',
            'time_spent_per_module',
        ],
    },
];
